import {
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useSelector } from 'react-redux';
import { useResource } from 'react-resource-router';
import { useDebounce } from 'use-debounce';

import { CurrentDataContext } from 'src/app/current-data';
import { MenuItem } from 'src/components/navigation/src/types';
import { StatsigFeatureKeys } from 'src/features';
import { useRecentData } from 'src/hooks/recent-data';
import { useSetState } from 'src/hooks/set-state';
import { useStatsigGate } from 'src/hooks/statsig';
import { useUserWorkspacePref } from 'src/hooks/use-pref';
import { accountNavigationResource } from 'src/sections/account/account-navigation-resource';
import {
  fetchForgeProjectNavigationData,
  forgeProjectNavigationResource,
} from 'src/sections/project/forge-project-navigation-resource';
import {
  projectNavigationResource,
  fetchProjectNavigationData,
} from 'src/sections/project/project-navigation-resource';
import {
  fetchForgeRepositoryNavigationData,
  forgeRepositoryNavigationResource,
} from 'src/sections/repository/forge-repository-navigation-resource';
import {
  repositoryNavigationResource,
  fetchRepoNavigationData,
} from 'src/sections/repository/repository-navigation-resource';
import { forgeWorkspaceNavigationResource } from 'src/sections/workspace/forge-workspace-navigation-resource';
import { workspaceNavigationResource } from 'src/sections/workspace/workspace-navigation-resource';
import { getCurrentUserIsAnonymous } from 'src/selectors/user-selectors';
import { ForgeNavigationData, NavigationData } from 'src/types/navigation';
import { escapeBbqlString } from 'src/utils/escape-bbql-string';
import { useMutation, useQuery } from 'src/utils/fb-query';
import { httpRequest } from 'src/utils/http-request';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { STARRED_OBJECTS_ONBOARDING_PREF_KEY } from './constants';
import {
  DEFAULT_SIDE_NAV_VIEW_MODE,
  MAX_STARRED_FETCH,
  MAX_VISIBLE_RECENTS,
  MAX_VISIBLE_STARRED,
  SIDE_NAV_VIEW_MODES,
  type SideNavViewMode,
} from './constants';
import { StarredItemsContext } from './starred-items-context';

export type ExpandableItemData<T> = {
  items: {
    isActive: boolean;
    loadNavigation: () => void;
    item: T;
    menuItems: MenuItem[] | undefined;
    isLoading: boolean;
  }[];
  isLoading: boolean;
};

type ExpandedNavigationItems<Item> = {
  items: Item[];
  loadNavigation: (item: Item) => Promise<MenuItem[]>;
  getKeyFromItem: (item: Item) => string;
  activeItemKey?: string;
  activeMenuItems?: MenuItem[];
  isLoading: boolean;
};

const useExpandedNavigationItems = <I>({
  items,
  loadNavigation,
  getKeyFromItem,
  activeItemKey,
  activeMenuItems,
  isLoading,
}: ExpandedNavigationItems<I>): ExpandableItemData<I> => {
  // This is a map from item key (e.g. repo uuid) to menu items for that item
  const [menuItemsForItem, setMenuItemsForItem] = useState<{
    [key: string]: MenuItem[];
  }>({});
  // While we are loading navigation for an item, keep track of that here
  const {
    set: loadingItems,
    add: addLoadingItem,
    remove: removeLoadingItem,
  } = useSetState<string>();

  // When we load data from a resource, add it to the menuItem state
  useEffect(() => {
    if (activeItemKey && activeMenuItems) {
      setMenuItemsForItem(prev => ({
        ...prev,
        [activeItemKey]: activeMenuItems,
      }));
    }
  }, [activeItemKey, activeMenuItems]);

  // This function loads navigation for a specific item, if it hasn't already
  // been loaded
  const loadNavigationForItem = useCallback(
    async (item: I) => {
      const itemKey = getKeyFromItem(item);
      if (menuItemsForItem[itemKey]) {
        return;
      }
      addLoadingItem(itemKey);
      const menuItems = await loadNavigation(item);
      setMenuItemsForItem(prev => ({
        ...prev,
        [itemKey]: menuItems,
      }));
      removeLoadingItem(itemKey);
    },
    [
      menuItemsForItem,
      loadNavigation,
      getKeyFromItem,
      addLoadingItem,
      removeLoadingItem,
    ]
  );

  const expandableItems = useMemo(() => {
    return items.map(item => {
      const itemKey = getKeyFromItem(item);
      return {
        item,
        // if menuItems hasn't been fetched yet, menuItems will be undefined
        menuItems: menuItemsForItem[itemKey],
        isLoading: loadingItems.has(itemKey),
        isActive: itemKey === activeItemKey,
        loadNavigation: () => loadNavigationForItem(item),
      };
    });
  }, [
    items,
    activeItemKey,
    getKeyFromItem,
    loadingItems,
    loadNavigationForItem,
    menuItemsForItem,
  ]);

  return {
    items: expandableItems,
    isLoading,
  };
};

const loadRepositoryNavigation = async (repo: BB.Repository) => {
  const [navData, forgeNavData] = await Promise.all([
    fetchRepoNavigationData(repo.full_name, false),
    fetchForgeRepositoryNavigationData(repo.full_name),
  ]);
  const menuItems = navData?.menuItems.sidebar || [];
  const forgeItems = forgeNavData?.forgeMenuItems || [];
  return [...menuItems, ...forgeItems];
};

export type ExpandableNavigationData<T> = {
  starredItems: ExpandableItemData<T>['items'];
  recentItems: ExpandableItemData<T>['items'];
  isRecentsLoading: boolean;
  isStarredLoading: boolean;
};

/**
 * Shared logic for splitting recent items into starred + non-starred lists
 * and enriching both with expandable navigation state.
 */
export const useStarredAndRecentNavigation = <
  T extends BB.Repository | BB.Project
>({
  recentItems,
  starredObjectType,
  activeItemKey,
  activeMenuItems,
  isRecentsLoading,
  loadNavigation,
  getKeyFromItem,
  currentItem,
  viewMode = DEFAULT_SIDE_NAV_VIEW_MODE,
}: {
  recentItems: T[];
  starredObjectType: StarredObjectType;
  activeItemKey?: string;
  activeMenuItems?: MenuItem[];
  isRecentsLoading: boolean;
  loadNavigation: (item: T) => Promise<MenuItem[]>;
  getKeyFromItem: (item: T) => string;
  currentItem?: T;
  viewMode?: SideNavViewMode;
}): ExpandableNavigationData<T> => {
  const starredContext = useContext(StarredItemsContext);

  const isStarred = starredContext?.isStarred;
  const recentItemsWithCurrent = useMemo(() => {
    if (!currentItem) return recentItems;

    const currentItemKey = getKeyFromItem(currentItem);
    return [
      currentItem,
      ...recentItems.filter(item => getKeyFromItem(item) !== currentItemKey),
    ];
  }, [currentItem, getKeyFromItem, recentItems]);

  const recentOnly = useMemo(() => {
    if (viewMode === SIDE_NAV_VIEW_MODES.MOST_RECENT_ONLY) {
      // For most recent only, use the current item context primarily;
      // fallback to first recent item if outside of item context.
      return currentItem ? [currentItem] : recentItems.slice(0, 1);
    }

    if (viewMode === SIDE_NAV_VIEW_MODES.STARRED_ONLY) {
      if (
        !currentItem ||
        (isStarred && isStarred(starredObjectType, currentItem))
      ) {
        return [];
      }
      return [currentItem];
    }

    if (!isStarred) return recentItemsWithCurrent;
    return recentItemsWithCurrent
      .filter(item => !isStarred(starredObjectType, item))
      .slice(0, MAX_VISIBLE_RECENTS);
  }, [
    currentItem,
    isStarred,
    recentItems,
    recentItemsWithCurrent,
    starredObjectType,
    viewMode,
  ]);

  const starredRepositories = starredContext?.starredRepositories;
  const starredProjects = starredContext?.starredProjects;
  const starred = useMemo(() => {
    // Don't show a starred section if most recent only is selected or missing
    // a starred context
    if (viewMode === SIDE_NAV_VIEW_MODES.MOST_RECENT_ONLY || !starredContext) {
      return [] as T[];
    }

    const allStarredItems =
      (starredObjectType === 'repositories'
        ? starredRepositories
        : starredProjects) ?? [];
    const visibleStarredItems = allStarredItems.slice(0, MAX_VISIBLE_STARRED);

    if (
      !currentItem ||
      !isStarred?.(starredObjectType, currentItem) ||
      visibleStarredItems.some(
        item => getKeyFromItem(item as T) === getKeyFromItem(currentItem)
      )
    ) {
      return visibleStarredItems as T[];
    }

    const currentStarredItem = allStarredItems.find(
      item => getKeyFromItem(item as T) === getKeyFromItem(currentItem)
    );

    if (!currentStarredItem) {
      return visibleStarredItems as T[];
    }

    if (visibleStarredItems.length === 0) {
      return [currentStarredItem as T];
    }

    return [
      ...(visibleStarredItems.slice(0, MAX_VISIBLE_STARRED - 1) as T[]),
      currentStarredItem as T,
    ];
  }, [
    currentItem,
    getKeyFromItem,
    isStarred,
    starredContext,
    starredObjectType,
    starredProjects,
    starredRepositories,
    viewMode,
  ]);

  const starredExpandable = useExpandedNavigationItems({
    items: starred,
    activeItemKey,
    activeMenuItems,
    isLoading: starredContext?.isLoading ?? false,
    loadNavigation,
    getKeyFromItem,
  });

  const recentExpandable = useExpandedNavigationItems({
    items: recentOnly,
    activeItemKey,
    activeMenuItems,
    isLoading: isRecentsLoading,
    loadNavigation,
    getKeyFromItem,
  });

  return {
    starredItems: starredExpandable.items,
    recentItems: recentExpandable.items,
    isRecentsLoading,
    isStarredLoading: starredContext?.isLoading ?? false,
  };
};

export const useRepositoryNavigation = (
  viewMode: SideNavViewMode = DEFAULT_SIDE_NAV_VIEW_MODE
): ExpandableNavigationData<BB.Repository> => {
  const { recentRepositories } = useRecentData();
  const { data, loading } = useResource(repositoryNavigationResource);
  const { data: forgeData, loading: forgeLoading } = useResource(
    forgeRepositoryNavigationResource
  );
  const getKeyFromItem = useCallback((repo: BB.Repository) => repo.uuid, []);
  const activeMenuItems = useMemo(() => {
    const menuItems = data?.menuItems.sidebar || [];
    const forgeItems = forgeData?.forgeMenuItems || [];
    return [...menuItems, ...forgeItems];
  }, [data, forgeData]);
  // Recompute when the visible repo order changes so the most recent item stays
  // aligned with the underlying recents source.
  const recentRepoKeys = JSON.stringify(
    recentRepositories.slice(0, MAX_VISIBLE_RECENTS).map(repo => repo.uuid)
  );
  const repositories = useMemo(
    () => recentRepositories,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recentRepoKeys]
  );

  return useStarredAndRecentNavigation({
    recentItems: repositories,
    starredObjectType: 'repositories',
    activeItemKey: data?.repository.uuid,
    activeMenuItems,
    isRecentsLoading: loading || forgeLoading,
    loadNavigation: loadRepositoryNavigation,
    getKeyFromItem,
    currentItem: data?.repository,
    viewMode,
  });
};

const loadProjectNavigation = async (project: BB.Project) => {
  const [navData, forgeNavData] = await Promise.all([
    fetchProjectNavigationData(project.workspace!.slug, project.key),
    fetchForgeProjectNavigationData(project.workspace!.slug, project.key),
  ]);
  const menuItems = navData?.menuItems.sidebar || [];
  const forgeItems = forgeNavData?.forgeMenuItems || [];
  return [...menuItems, ...forgeItems];
};

export const useProjectNavigation = (
  viewMode: SideNavViewMode = DEFAULT_SIDE_NAV_VIEW_MODE
): ExpandableNavigationData<BB.Project> => {
  const { recentProjects } = useRecentData();
  const { data, loading } = useResource(projectNavigationResource);
  const { data: forgeData, loading: forgeLoading } = useResource(
    forgeProjectNavigationResource
  );
  const getKeyFromItem = useCallback((project: BB.Project) => project.uuid, []);
  const activeMenuItems = useMemo(() => {
    const menuItems = data?.menuItems.sidebar || [];
    const forgeItems = forgeData?.forgeMenuItems || [];
    return [...menuItems, ...forgeItems];
  }, [data, forgeData]);
  // Recompute when the visible project order changes so the most recent item
  // stays aligned with the underlying recents source.
  const recentProjectKeys = JSON.stringify(
    recentProjects.slice(0, MAX_VISIBLE_RECENTS).map(project => project.uuid)
  );
  const projects = useMemo(
    () => recentProjects,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recentProjectKeys]
  );

  return useStarredAndRecentNavigation({
    recentItems: projects,
    starredObjectType: 'projects',
    activeItemKey: data?.project.uuid,
    activeMenuItems,
    isRecentsLoading: loading || forgeLoading,
    loadNavigation: loadProjectNavigation,
    getKeyFromItem,
    currentItem: data?.project,
    viewMode,
  });
};

// returns forge nav data for the current resource (order based on specificity)
// repo > project > workspace
export const useForgeNavData = (): ForgeNavigationData | null => {
  // See the note in `useMenuItems` about why we want this in its own state
  const [forgeData, setForgeData] = useState<ForgeNavigationData | null>(null);

  const { data: workspaceForgeData } = useResource(
    forgeWorkspaceNavigationResource
  );

  const { data: repoForgeData } = useResource(
    forgeRepositoryNavigationResource
  );
  const { data: projectForgeData } = useResource(
    forgeProjectNavigationResource
  );

  useEffect(() => {
    if (repoForgeData) {
      setForgeData(repoForgeData);
    } else if (projectForgeData) {
      setForgeData(projectForgeData);
    } else if (workspaceForgeData) {
      setForgeData(workspaceForgeData);
    }
  }, [repoForgeData, projectForgeData, workspaceForgeData]);

  return forgeData;
};

// returns workspace scoped forge items from the the available resource
export const useWorkspaceForgeMenuItems = (): MenuItem[] => {
  // See the note in `useMenuItems` about why we want this in its own state
  const [forgeData, setForgeData] = useState<MenuItem[]>([]);
  const forgeNavData = useForgeNavData();

  useEffect(() => {
    if (forgeNavData?.workspaceForgeMenuItems) {
      setForgeData(forgeNavData.workspaceForgeMenuItems);
    }
  }, [forgeNavData]);

  return forgeData;
};

export const useMenuItems = () => {
  // We explicitly want to keep this data in its own state. There's a slight
  // lag vs. using a memo, but the important part is that we don't want to
  // clear the state while navigating. If we use a memo then we have nothing to
  // display when going from one repository to another.
  const [menuItems, setMenuItems] = useState<
    NavigationData['menuItems'] | null
  >(null);

  const { data: repositoryData, loading: isRepositoryLoading } = useResource(
    repositoryNavigationResource
  );
  const { data: projectData, loading: isProjectLoading } = useResource(
    projectNavigationResource
  );
  const { data: workspaceData, loading: isWorkspaceLoading } = useResource(
    workspaceNavigationResource
  );
  const { data: accountNavData, loading: isAccountNavLoading } = useResource(
    accountNavigationResource
  );
  // forge menu items come from a sep API request. This hook returns the menu items
  // that should be included in the workspace scoped apps
  const workspaceForgeMenuItems = useWorkspaceForgeMenuItems();

  useEffect(() => {
    if (!isRepositoryLoading && repositoryData) {
      setMenuItems(repositoryData.menuItems);
    } else if (!isProjectLoading && projectData) {
      setMenuItems(projectData.menuItems);
    } else if (!isWorkspaceLoading && workspaceData) {
      setMenuItems(workspaceData.menuItems);
    } else if (!isAccountNavLoading && accountNavData) {
      setMenuItems(accountNavData.menuItems);
    }
  }, [
    isRepositoryLoading,
    repositoryData,
    isWorkspaceLoading,
    workspaceData,
    isProjectLoading,
    projectData,
    isAccountNavLoading,
    accountNavData,
  ]);

  const allItems = useMemo(() => {
    // if there are forge workspace-scoped items, add them to the secondary items
    // so they can be rendered under the workspace "Apps" folder with Connect apps
    if (
      menuItems &&
      workspaceForgeMenuItems &&
      workspaceForgeMenuItems.length > 0
    ) {
      return {
        ...menuItems,
        secondary: [...menuItems.secondary, ...workspaceForgeMenuItems],
      };
    }
    // otherwise return the unmodified menu items
    return menuItems;
  }, [workspaceForgeMenuItems, menuItems]);

  return allItems;
};

export type StarredObjectType = 'repositories' | 'projects';

type StarredItem = {
  type: 'repository' | 'project';
  resource: BB.Repository | BB.Project;
};

type StarredResponse = {
  values: StarredItem[];
  next?: string;
};

type FetchStarredParams = {
  workspaceSlug: string;
  objectType: StarredObjectType;
};

const STARRED_ITEMS_FIELDS =
  '+values.resource.is_private,+values.resource.workspace.slug';

const STARRED_SEARCH_FIELDS: Record<StarredObjectType, string[]> = {
  repositories: ['repository.name'],
  projects: ['project.name', 'project.key'],
};

const fetchInitialStarredItems = async ({
  workspaceSlug,
  objectType,
}: FetchStarredParams): Promise<{
  values: (BB.Repository | BB.Project)[];
  type: StarredObjectType;
}> => {
  const params = new URLSearchParams({
    object_type: objectType,
    // Fetch up to MAX_STARRED_FETCH so isStarred filtering covers items beyond
    // the visible MAX_VISIBLE_STARRED without following pagination next links.
    pagelen: `${MAX_STARRED_FETCH}`,
    // Some starred payload variants omit privacy fields unless explicitly requested.
    // Request them so lock icons can render correctly for initially-starred items.
    fields: STARRED_ITEMS_FIELDS,
  });
  const response = await httpRequest<StarredResponse>().get(
    `/!api/internal/workspaces/${workspaceSlug}/starred?${params.toString()}`
  );
  return {
    values: response.values.map(item => item.resource),
    type: objectType,
  };
};

/**
 * Fetches starred repositories and projects for the current workspace when nav mounts.
 * Used to provide initial starred state to navigation items.
 * Provides setStarred to sync context when user toggles star.
 * Provides isStarred to easily check if a repository or project is starred.
 */
export const useFetchStarredItemsForWorkspace = () => {
  const { workspace } = useContext(CurrentDataContext);
  const workspaceSlug = workspace?.slug ?? '';

  const [starredRepositories, setStarredRepositories] = useState<
    BB.Repository[]
  >([]);
  const [starredProjects, setStarredProjects] = useState<BB.Project[]>([]);

  const onError = useCallback(
    (error: Error) => {
      captureExceptionWithTags(error, {
        workspaceSlug,
        source: 'useFetchStarredItemsForWorkspace',
      });
    },
    [workspaceSlug]
  );

  const onSuccess = useCallback(
    (result: {
      values: (BB.Repository | BB.Project)[];
      type: StarredObjectType;
    }) => {
      const { values, type } = result;
      if (type === 'repositories') {
        setStarredRepositories(values as BB.Repository[]);
      } else if (type === 'projects') {
        setStarredProjects(values as BB.Project[]);
      }
    },
    []
  );

  const { isPending: isRepositoriesPending } = useQuery(
    fetchInitialStarredItems,
    { workspaceSlug, objectType: 'repositories' },
    {
      enabled: !!workspace?.slug,
      onError,
      onSuccess,
    }
  );

  const { isPending: isProjectsPending } = useQuery(
    fetchInitialStarredItems,
    { workspaceSlug, objectType: 'projects' },
    {
      enabled: !!workspace?.slug,
      onError,
      onSuccess,
    }
  );

  const setStarred = useCallback(
    (
      objectType: StarredObjectType,
      resource: BB.Repository | BB.Project,
      isStarred: boolean
    ) => {
      const applyStarredItems = <T extends { uuid: string }>(prev: T[]) =>
        isStarred
          ? [
              resource as unknown as T,
              ...prev.filter(item => item.uuid !== resource.uuid),
            ]
          : prev.filter(item => item.uuid !== resource.uuid);

      if (objectType === 'repositories') {
        setStarredRepositories(applyStarredItems);
      } else {
        setStarredProjects(applyStarredItems);
      }
    },
    [setStarredRepositories, setStarredProjects]
  );

  const isStarred = useCallback(
    (objectType: StarredObjectType, resource: BB.Repository | BB.Project) =>
      objectType === 'repositories'
        ? starredRepositories.some(repo => repo.uuid === resource.uuid)
        : starredProjects.some(project => project.uuid === resource.uuid),
    [starredRepositories, starredProjects]
  );

  return {
    starredRepositories,
    starredProjects,
    isLoading:
      !!workspace?.slug && (isRepositoriesPending || isProjectsPending),
    setStarred,
    isStarred,
  };
};

/**
 * Fetches and manages starred items for the starred flyout menu in the side nav.
 * Provides a paginated, scrollable list with support for filtering by type and
 * optimistic unstar with error recovery.
 *
 * When no objectTypeFilter is provided, fetches all starred items (repos + projects).
 * When a filter is selected, only fetches starred items of that type.
 */
const buildStarredFlyoutUrl = (
  workspaceSlug: string,
  objectType?: StarredObjectType,
  query?: string
) => {
  const params = new URLSearchParams({
    pagelen: MAX_STARRED_FETCH.toString(),
  });
  if (objectType) {
    params.set('object_type', objectType);
  }
  if (query) {
    const fields = objectType
      ? STARRED_SEARCH_FIELDS[objectType]
      : Object.values(STARRED_SEARCH_FIELDS).flat();
    const escaped = escapeBbqlString(query);
    params.set('q', fields.map(f => `${f}~"${escaped}"`).join(' OR '));
  }
  return `/!api/internal/workspaces/${workspaceSlug}/starred?${params.toString()}`;
};

const fetchStarredFlyoutItems = (args: {
  workspaceSlug: string;
  objectTypeFilter?: StarredObjectType;
  searchText?: string;
}): Promise<StarredResponse> => {
  return httpRequest<StarredResponse>().get(
    buildStarredFlyoutUrl(
      args.workspaceSlug,
      args.objectTypeFilter,
      args.searchText
    )
  );
};

type ToggleStarArgs = {
  workspaceSlug: string;
  objectType: StarredObjectType;
  resource: BB.Repository | BB.Project;
  isStarred: boolean;
};

/**
 * Toggle the starred state of a repository or project in a workspace.
 * @param args.workspaceSlug - The workspace slug
 * @param args.objectType - Either 'repositories' or 'projects'
 * @param args.resource - The repository or project instance
 * @param args.isStarred - The current starred state (true = unstar, false = star)
 * @returns The new starred state after the toggle
 */
const toggleStarObject = async ({
  workspaceSlug,
  objectType,
  resource,
  isStarred,
}: ToggleStarArgs): Promise<boolean> => {
  const method = isStarred ? 'delete' : 'put';
  await httpRequest()[method](
    `/!api/internal/workspaces/${workspaceSlug}/starred/${objectType}/${resource.uuid}`
  );
  return !isStarred;
};

export const useStarredFlyoutItems = (
  objectTypeFilter?: StarredObjectType,
  searchText?: string
) => {
  const { workspace } = useContext(CurrentDataContext);
  const workspaceSlug = workspace?.slug ?? '';
  const starredContext = useContext(StarredItemsContext);

  const [items, setItems] = useState<StarredItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const nextUrl = useRef<string | undefined>(undefined);
  const isLoadingMore = useRef(false);
  const loadMoreGeneration = useRef(0);
  const [debouncedSearchText] = useDebounce(searchText, 300);

  const queryArgs = useMemo(
    () => ({
      workspaceSlug,
      objectTypeFilter,
      searchText: debouncedSearchText,
    }),
    [workspaceSlug, objectTypeFilter, debouncedSearchText]
  );

  const queryResult = useQuery(fetchStarredFlyoutItems, queryArgs, {
    enabled: !!workspaceSlug,
    onSuccess: (response: StarredResponse) => {
      setItems(response.values);
      nextUrl.current = response.next;
      setHasMore(!!response.next);
      // Bump generation so any in-flight loadMore is invalidated
      loadMoreGeneration.current++;
    },
    onError: (error: Error) => {
      captureExceptionWithTags(error, {
        workspaceSlug,
        source: 'useStarredFlyoutItems',
      });
    },
  });

  const isLoading = queryResult.status === 'pending';

  const loadMore = useCallback(async () => {
    if (isLoadingMore.current || !nextUrl.current) return;

    const generation = loadMoreGeneration.current;
    isLoadingMore.current = true;
    try {
      const response = await httpRequest<StarredResponse>().get(
        nextUrl.current
      );
      // Ignore stale responses if filter/search changed during load
      if (generation !== loadMoreGeneration.current) return;
      setItems(prev => [...prev, ...response.values]);
      nextUrl.current = response.next;
      setHasMore(!!response.next);
    } catch (error) {
      if (generation !== loadMoreGeneration.current) return;
      captureExceptionWithTags(error as Error, {
        workspaceSlug,
        source: 'useStarredFlyoutItems.loadMore',
      });
    } finally {
      if (generation === loadMoreGeneration.current) {
        isLoadingMore.current = false;
      }
    }
  }, [workspaceSlug]);

  const [unstarringIds, setUnstarringIds] = useState<Set<string>>(new Set());

  const unstarItem = useCallback(
    async (
      objectType: StarredObjectType,
      resource: BB.Repository | BB.Project
    ) => {
      const restoredItem: StarredItem = {
        type: objectType === 'repositories' ? 'repository' : 'project',
        resource,
      };
      setUnstarringIds(prev => new Set(prev).add(resource.uuid));
      // Optimistically remove the item from the list
      setItems(prev =>
        prev.filter(item => item.resource.uuid !== resource.uuid)
      );
      try {
        await toggleStarObject({
          workspaceSlug,
          objectType,
          resource,
          isStarred: true,
        });
        // Sync with the side nav's starred items context
        starredContext?.setStarred(objectType, resource, false);
      } catch (error) {
        // Restore the item if the request failed
        setItems(prev => [...prev, restoredItem]);
        captureExceptionWithTags(error as Error, {
          workspaceSlug,
          source: 'useStarredFlyoutItems.unstarItem',
        });
      } finally {
        setUnstarringIds(prev => {
          const next = new Set(prev);
          next.delete(resource.uuid);
          return next;
        });
      }
    },
    [workspaceSlug, starredContext]
  );

  const isUnstarring = (uuid: string) => unstarringIds.has(uuid);

  return {
    items,
    isLoading,
    hasMore,
    loadMore,
    unstarItem,
    isUnstarring,
  };
};

interface UseStarredObjectsParams {
  workspaceSlug?: string;
  objectType?: StarredObjectType;
  resource?: BB.Repository | BB.Project;
  starredState?: boolean;
  onStarredChange?: (isStarred: boolean) => void;
}

/**
 * Hook for managing starred state of a repository or project.
 * Handles loading state and API calls for starring/unstarring.
 */
export const useStarredObjects = ({
  workspaceSlug,
  objectType,
  resource,
  starredState = false,
  onStarredChange,
}: UseStarredObjectsParams) => {
  const [isStarred, setIsStarred] = useState(starredState);

  // Sync local state when starredState changes (e.g. external updates when starring can happen in other places)
  useEffect(() => {
    setIsStarred(starredState);
  }, [starredState]);

  const { mutate, isPending } = useMutation<boolean, ToggleStarArgs>(
    toggleStarObject,
    {
      onSuccess: newIsStarred => {
        setIsStarred(newIsStarred);
        onStarredChange?.(newIsStarred);
      },
      onError: error => {
        captureExceptionWithTags(error, {
          workspaceSlug: workspaceSlug ?? '',
          objectType: objectType ?? '',
          uuid: resource?.uuid ?? '',
        });
      },
    }
  );

  const toggleStar = useCallback(() => {
    if (!workspaceSlug || !objectType || !resource || isPending) {
      return;
    }
    mutate({ workspaceSlug, objectType, resource, isStarred });
  }, [workspaceSlug, objectType, resource, isStarred, isPending, mutate]);

  return {
    isStarred,
    isLoading: isPending,
    toggleStar,
  };
};

/**
 * Manages the starred-objects onboarding spotlight state.
 *
 * The spotlight is shown once per workspace (for the first active nav item the
 * user encounters) and persisted via a workspace-scoped user preference so it
 * doesn't reappear after dismissal or page reload.
 *
 * While the preference is still loading we conservatively treat the spotlight
 * as inactive so we don't flash it for users who have already seen it.
 *
 * Pass enabled=false (when the spotlight gate is off) to skip the API call entirely.
 */
export function useStarredOnboarding(enabled: boolean = true) {
  const { workspace } = useContext(CurrentDataContext);
  const localDismissalScope = workspace?.uuid ?? '';
  const [dismissedScope, setDismissedScope] = useState<string | null>(null);
  const [storedValue, updateStoredValue, isLoading] = useUserWorkspacePref(
    STARRED_OBJECTS_ONBOARDING_PREF_KEY,
    null,
    enabled
  );

  const hasSeen =
    storedValue === 'true' || dismissedScope === localDismissalScope;

  // Spotlight is active when: enabled, preference has finished loading, and the
  // user has not yet dismissed it.
  const isSpotlightActive = enabled && !isLoading && !hasSeen;

  const dismissSpotlight = useCallback(() => {
    setDismissedScope(localDismissalScope);
    updateStoredValue('true');
  }, [localDismissalScope, updateStoredValue]);

  return [isSpotlightActive, dismissSpotlight] as const;
}

// handles feature flag checks/auth related to the starred objects project
export const useShowStarredObjects = () => {
  const isAnonymous = useSelector(getCurrentUserIsAnonymous);
  const isStarredObjectsEnabled = useStatsigGate(
    StatsigFeatureKeys.bbcStarredObjects
  );
  const isStarredObjectsSpotlightEnabled = useStatsigGate(
    StatsigFeatureKeys.bbcStarredObjectsSpotlight
  );
  const showStarredObjects = isStarredObjectsEnabled && !isAnonymous;
  const showStarredObjectsSpotlight =
    isStarredObjectsSpotlightEnabled && showStarredObjects;

  return {
    isStarredObjectsEnabled,
    showStarredObjects,
    showStarredObjectsSpotlight,
  };
};

export const useLoadRecents = () => {
  // We want to load recent repositories and projects only once each, and that can be either when
  // the user expands the relevant menu item, or if that item is active on mount.
  const { loadRecentRepositories, loadRecentProjects } = useRecentData();

  const loadedRepositoriesRef = useRef(false);
  const loadedProjectsRef = useRef(false);

  const loadRepositoriesOnce = useCallback(() => {
    if (loadedRepositoriesRef.current) return;
    loadedRepositoriesRef.current = true;
    loadRecentRepositories(false);
  }, [loadRecentRepositories]);

  const loadProjectsOnce = useCallback(() => {
    if (loadedProjectsRef.current) return;
    loadedProjectsRef.current = true;
    loadRecentProjects();
  }, [loadRecentProjects]);

  return {
    loadRepositoriesOnce,
    loadProjectsOnce,
  };
};
